import { renderUtils } from "../render/render.js";
import { AttributeUtils } from "../attribute/attribute.js";

/**
 * @throws {Error} Throws error if `componentName` is not set
 * @example
 * static componentName = 'sample-component';
 */
export class EDSComponent extends HTMLElement {
  /**
   * It is used for namespacing the data-test attribute on testmode
   * @static
   */
  static componentName = "";

  /**
   * @static
   * @returns {string}
   */
  static tagName() {
    return `e-${this.componentName}`;
  }


  /**
   * You must define either the `render` of the `_render` methods in your component.
   * Its debounced version will be stored as the requestRender method.
   * @type {import('./../render/lib/debounce.js').DebounceCallbackWithMethods<any> & (() => Promise<void>)}
   */
  //@ts-expect-error
  requestRender = undefined;


  /**
   * @type {object}
   */
  refs = {};

  /**
   * @type {{ refs: object }}
   */
  test = { refs: {} };


  /**
   * Creates an instance of EDSComponent.
   * @constructor
   * @param {*} params
   */
  constructor(params) {

    /** @type {EDSComponent} */
    //@ts-expect-error
    const element = super(params);
    //@ts-expect-error
    if (this.constructor.componentName === "") {
      throw new Error("The componentName static property must be defined!");
    }
    //@ts-expect-error
    const renderFn = element.render || element._render || (() => {});

    element.requestRender = renderUtils.debounce.create((...args) => {
      renderFn.call(this, ...args);
    }, this);
    element.init();
    return element;
  }

  /**
   * The init method is used for initializing your component. It is run inside the constructor.
   * @abstract
   */
  init() {}


  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    const transformedName = name.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    //@ts-expect-error
    this[transformedName] = newValue;
  }

  /**
   * Helper method for reflecting the value of the set property as string to the attribute as well
   * @param {string} name - Name of the attribute
   * @param {string} value - The value to be reflected
   *
   * @example
   * set type(value) {
   *   this.state.type = value;
   *   this.reflectStringAttribute('type', this.state.type);
   * }
   */
  reflectStringAttribute(name, value) {
    if (value === null && this.hasAttribute(name)) {
      this.removeAttribute(name);
    } else if (this.getAttribute(name) !== value) {
      this.setAttribute(name, value);
    }
  }


  /**
   * * Helper method for reflecting the value of the set property as boolean to the attribute as well
   * @param {string} name - Name of the attribute
   * @param {boolean} value - Value going to reflected
   * @example
   * set disabled(value) {
   *   this.state.isDisabled = value;
   *   this.reflectBooleanAttribute('disabled', this.state.isDisabled);
   * }
   */
  reflectBooleanAttribute(name, value) {
    const attributeValue = AttributeUtils.convertToBoolean(value);

    if (attributeValue === false && this.hasAttribute(name)) {
      this.removeAttribute(name);
    } else if (attributeValue === true && !this.hasAttribute(name)) {
      this.setAttribute(name, "");
    }
  }


  /**
   * It saves the node reference under the `this.ref` and `this.test.ref` objects with the given path.
   * @param {Array<string>} path - Array of string
   * @param {{ testOnly: boolean; }} [options={ testOnly: false }] - If it is true the reference is stored only under `this.test.refs`
   * @returns {(node: HTMLElement) => void}
   *
   * @example <caption>Simple usage</caption>
   * // this.refs.popup.input
   * `<input ref=${this.createReference(['input'])}>`
   *
   * @example <caption>Nested reference</caption>
   * // this.refs.popup.input
   * `<input ref=${this.createReference(['popup', 'input'])}>`
   *
   * @example <caption>Nested reference</caption>
   * // If `window.e.testMode` is true the reference will be exposed as a `data-testid`
   * // <input data-testid="sample-component.popup.input">
   * `<input ref=${this.createReference(['popup', 'input'])}>`
   */
  createReference(path, options = { testOnly: false }) {
    const targets = [
      ...(!options.testOnly ? [this.refs] : []),
      //@ts-expect-error Need to define window.e globally
      ...(window?.e?.testMode ? [this.test.refs] : [])
    ];

    return (node) => {
      //@ts-expect-error Need to define window.e globally
      if (window?.e?.testMode) {
        //@ts-expect-error
        node.dataset.testid = `${this.constructor.componentName}.${path.join(".")}`;
      }

      targets.forEach((target) => {
        this.#setPropertyByPath(target, path, node);
      });
    };
  }

  /**
   * Removes element specified with the containerSelector from the component. Used to clean up element before render.
   * @param {string} containerSelector - querySelector for the wrapper element
   *
   * @example <caption>After reattaching to the DOM, you need to cleanup the state of the elements inside the component</caption>
   * connectedCallback() {
   * this._cleanupContainer('e-actionlist-wrapper');
   * this.requestRender();
   * }
   */
  _cleanupContainer(containerSelector) {
    const container = this.querySelector(containerSelector);
    if (container) {
      this.removeChild(container);
    }
  }

  /**
   * @param {*} object
   * @param {*} path
   * @param {unknown} value
   */
  #setPropertyByPath(object, path, value) {
    let currentProperty = object;

    for (const [index, property] of path.entries()) {
      const isPropertyLast = index === path.length - 1;

      if (isPropertyLast) {
        currentProperty[property] = value;
        return;
      }

      const nextProperty = path[index + 1];
      const isNextPropertyValidIndex = Number.isInteger(nextProperty) && nextProperty >= 0;
      const defaultValueForProperty = isNextPropertyValidIndex ? [] : {};
      const isCurrentPropertyValid =
        typeof currentProperty[property] === "object" && currentProperty[property] !== null;

      currentProperty[property] = isCurrentPropertyValid
        ? currentProperty[property]
        : defaultValueForProperty;
      currentProperty = currentProperty[property];
    }
  }
}
